import React, { useState } from 'react';
import { 
  Calendar,
  CreditCard,
  Clock,
  AlertCircle,
  CheckCircle2,
  XCircle,
  DollarSign,
  Filter,
  Search,
  BarChart2,
  ArrowUpCircle,
  Ban,
  Info,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';

// Types
interface InstallmentPayment {
  id: string;
  customerName: string;
  amount: number;
  totalAmount: number;
  currency: string;
  status: 'pending' | 'completed' | 'overdue' | 'cancelled';
  startDate: string;
  nextPaymentDate: string;
  totalInstallments: number;
  completedInstallments: number;
  paymentDay: number;
  interestRate: number;
  product: {
    name: string;
    id: string;
  };
}

const DEFAULT_CURRENCY = {
  code: 'GHS',
  symbol: '₵'
};

// Mock data
const MOCK_PAYMENTS: InstallmentPayment[] = [
  {
    id: 'INS001',
    customerName: 'John Doe',
    amount: 500,
    totalAmount: 6000,
    currency: 'GHS',
    status: 'pending',
    startDate: '2024-02-01',
    nextPaymentDate: '2024-03-01',
    totalInstallments: 12,
    completedInstallments: 1,
    paymentDay: 1,
    interestRate: 5,
    product: {
      name: 'Toyota Camry 2020',
      id: 'PRD001'
    }
  },
  {
    id: 'INS002',
    customerName: 'Jane Smith',
    amount: 800,
    totalAmount: 4000,
    currency: 'GHS',
    status: 'overdue',
    startDate: '2024-01-15',
    nextPaymentDate: '2024-02-15',
    totalInstallments: 5,
    completedInstallments: 1,
    paymentDay: 15,
    interestRate: 3,
    product: {
      name: 'Honda Civic 2019',
      id: 'PRD002'
    }
  },
  // Add more mock payments to demonstrate pagination
  {
    id: 'INS003',
    customerName: 'Mike Johnson',
    amount: 600,
    totalAmount: 5000,
    currency: 'GHS',
    status: 'completed',
    startDate: '2023-12-01',
    nextPaymentDate: '2024-01-01',
    totalInstallments: 8,
    completedInstallments: 8,
    paymentDay: 1,
    interestRate: 4,
    product: {
      name: 'MacBook Pro',
      id: 'PRD003'
    }
  },
  {
    id: 'INS004',
    customerName: 'Emily Davis',
    amount: 750,
    totalAmount: 4500,
    currency: 'GHS',
    status: 'pending',
    startDate: '2024-03-15',
    nextPaymentDate: '2024-04-15',
    totalInstallments: 6,
    completedInstallments: 1,
    paymentDay: 15,
    interestRate: 3.5,
    product: {
      name: 'Samsung TV',
      id: 'PRD004'
    }
  }
];

const InstallmentManagement = () => {
      useBreadcrumbs([
            { label: 'Escrow Management', href: '#' },
            { label: 'Escrow Installment Payment Management' }
          ]);
  // State
  const [payments, setPayments] = useState<InstallmentPayment[]>(MOCK_PAYMENTS);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPayment, setSelectedPayment] = useState<InstallmentPayment | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(2);

  // Calculate stats
  const stats = {
    totalActive: payments.filter(p => p.status === 'pending').length,
    totalOverdue: payments.filter(p => p.status === 'overdue').length,
    totalAmount: payments.reduce((acc, p) => acc + p.totalAmount, 0),
    completionRate: (payments.filter(p => p.status === 'completed').length / payments.length) * 100
  };

  // Filter payments
  const filteredPayments = payments.filter(payment => {
    const matchesStatus = filterStatus === 'all' || payment.status === filterStatus;
    const matchesSearch = 
      payment.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      payment.id.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPayments.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);

  // Pagination handlers
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-blue-100 text-blue-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'overdue':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'cancelled':
        return <Ban className="h-4 w-4 text-gray-500" />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow">
          {/* Header */}
          <div className="border-b border-gray-200 px-6 py-4">
            <h1 className="text-xl font-semibold text-gray-900">Installment Payment Management</h1>
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 p-6 border-b border-gray-200">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center">
                <CreditCard className="h-6 w-6 text-blue-500" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Active Payments</p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">
                    {stats.totalActive}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center">
                <AlertCircle className="h-6 w-6 text-red-500" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Overdue Payments</p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">
                    {stats.totalOverdue}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center">
                <DollarSign className="h-6 w-6 text-green-500" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Amount</p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">
                    {DEFAULT_CURRENCY.symbol}{stats.totalAmount.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center">
                <BarChart2 className="h-6 w-6 text-purple-500" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Completion Rate</p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">
                    {stats.completionRate.toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0 sm:space-x-4">
              <div className="flex items-center space-x-4 w-full sm:w-auto">
                <div className="relative w-full sm:w-64">
                  <input
                    type="text"
                    placeholder="Search payments..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                </div>
                <div className="relative w-full sm:w-48">
                  <select
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="overdue">Overdue</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  <Filter className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>
          </div>

          {/* Payments Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Progress
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Next Payment
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentItems.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {payment.customerName}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {payment.id}
                          </div>
                          <div className="text-sm text-gray-500">
                            {payment.product.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {DEFAULT_CURRENCY.symbol}{payment.amount.toLocaleString()} /mo
                      </div>
                      <div className="text-sm text-gray-500">
                        Total: {DEFAULT_CURRENCY.symbol}{payment.totalAmount.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-1">
                          <div className="h-2 bg-gray-200 rounded-full">
                            <div 
                              className="h-2 bg-blue-500 rounded-full" 
                              style={{ 
                                width: `${(payment.completedInstallments / payment.totalInstallments) * 100}%` 
                              }}
                            />
                          </div>
                        </div>
                        <span className="ml-2 text-sm text-gray-500">
                          {payment.completedInstallments}/{payment.totalInstallments}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(payment.status)}`}>
                        <span className="flex items-center">
                          {getStatusIcon(payment.status)}
                          <span className="ml-1">
                            {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                          </span>
                        </span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(payment.nextPaymentDate).toLocaleDateString()}
                      </div>
                      <div className="text-sm text-gray-500">
                        Day {payment.paymentDay}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedPayment(payment);
                          setShowDetailsModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={prevPage}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={nextPage}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{' '}
                    <span className="font-medium">
                      {indexOfFirstItem + 1}
                    </span>{' '}
                    to{' '}
                    <span className="font-medium">
                      {Math.min(indexOfLastItem, filteredPayments.length)}
                    </span>{' '}
                    of{' '}
                    <span className="font-medium">
                      {filteredPayments.length}
                    </span>{' '}
                    results
                  </p>
                </div>
                <div>
                  <nav 
                    className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" 
                    aria-label="Pagination"
                  >
                    <button
                      onClick={prevPage}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                    </button>
                    {[...Array(totalPages)].map((_, index) => (
                      <button
                        key={index}
                        onClick={() => paginate(index + 1)}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium ${
                          currentPage === index + 1 
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' 
                            : 'text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {index + 1}
                      </button>
                    ))}
                    <button
                      onClick={nextPage}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRight className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Details Modal (Placeholder) */}
        {showDetailsModal && selectedPayment && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Payment Details for {selectedPayment.customerName}
                  </h3>
                  <div className="mt-4">
                    <p><strong>Product:</strong> {selectedPayment.product.name}</p>
                    <p><strong>Total Amount:</strong> {DEFAULT_CURRENCY.symbol}{selectedPayment.totalAmount.toLocaleString()}</p>
                    <p><strong>Status:</strong> {selectedPayment.status}</p>
                    <p><strong>Progress:</strong> {selectedPayment.completedInstallments}/{selectedPayment.totalInstallments}</p>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={() => setShowDetailsModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstallmentManagement;